// frontend/src/hooks/bookingManagement/useBookingManagement.js

import { useCallback, useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import { toast } from 'react-toastify';
import { getRoutes as fetchEntityRoutes } from '../../services/routeService';
import translationService from '../../services/translationService';

const useBookingManagement = ({ currentEntity, entitySettings, i18n, t }) => {
  const [routes, setRoutes] = useState([]);
  const [referenceDataMap, setReferenceDataMap] = useState({});
  const [segmentDataMap, setSegmentDataMap] = useState({}); // Segment times for baseline ETA
  const [userBookings, setUserBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAllReferenceData = useCallback(
    async (routesForFetch) => {
      if (!currentEntity?.entityId || !routesForFetch?.length) {
        setReferenceDataMap({});
        return {};
      }

      const archivedType = currentEntity?.archived_type || 'representative';
      const map = {};

      await Promise.all(
        routesForFetch.map(async (route) => {
          try {
            const response = await axios.get(
              `/api/routes/${currentEntity.entityId}/route/${route.id}/reference-duration`,
              {
                params: { archived_type: archivedType },
              },
            );

            if (response.data.success && response.data.hasReferenceRoute) {
              map[route.id] = response.data.referenceData;
            }
          } catch {
            console.log(`No reference route data available for route ${route.id}`);
          }
        }),
      );

      setReferenceDataMap(map);
      return map;
    },
    [currentEntity?.archived_type, currentEntity?.entityId],
  );

  // Fetch segment times (OpenRouteService baseline) for all routes
  const fetchAllSegmentData = useCallback(
    async (routesForFetch) => {
      if (!currentEntity?.entityId || !routesForFetch?.length) {
        setSegmentDataMap({});
        return {};
      }

      const map = {};

      await Promise.all(
        routesForFetch.map(async (route) => {
          try {
            const response = await axios.get(
              `/api/segment-matrix/${currentEntity.entityId}/routes/${route.id}/segments`,
              {
                params: {
                  standardOnly: true,
                },
              },
            );

            if (response.data.success && response.data.data?.length > 0) {
              map[route.id] = response.data.data;
            }
          } catch {
            // Segment data may not exist yet - this is not an error condition
            console.log(`No segment data available for route ${route.id}`);
          }
        }),
      );

      setSegmentDataMap(map);
      return map;
    },
    [currentEntity?.entityId],
  );

  const applyRouteTranslations = useCallback(
    async (existingRoutes) => {
      const defaultLanguage = entitySettings?.entity_default_language || currentEntity?.entity_default_language || 'en';
      const shouldTranslateContent = entitySettings?.multi_language_enabled === true && i18n.language !== defaultLanguage;

      if (!shouldTranslateContent || !existingRoutes?.length) {
        return existingRoutes;
      }

      return translationService.translateRoutesWithWaypoints(existingRoutes, {
        entityId: currentEntity.entityId,
        userLanguage: i18n.language,
        defaultLanguage,
        mode: 'replace',
      });
    },
    [
      currentEntity?.entityId,
      currentEntity?.entity_default_language,
      entitySettings?.entity_default_language,
      entitySettings?.multi_language_enabled,
      i18n.language,
    ],
  );

  const applyBookingTranslations = useCallback(
    async (bookings) => {
      const defaultLanguage = entitySettings?.entity_default_language || currentEntity?.entity_default_language || 'en';
      const shouldTranslateContent = entitySettings?.multi_language_enabled === true && i18n.language !== defaultLanguage;

      if (!shouldTranslateContent || !bookings?.length) {
        return bookings;
      }

      const uniqueRouteIds = [...new Set(bookings.map((b) => b.route_id).filter(Boolean))];
      const uniqueWaypointIds = [
        ...new Set(
          bookings
            .flatMap((booking) => [booking.pickup_stop_id, booking.dropoff_stop_id])
            .filter((id) => id !== undefined && id !== null),
        ),
      ];

      if (!uniqueRouteIds.length && !uniqueWaypointIds.length) {
        return bookings;
      }

      const routeItems = uniqueRouteIds.map((routeId) => ({
        id: routeId,
        fields: {
          name: bookings.find((b) => b.route_id === routeId)?.route_name || '',
        },
      }));

      const waypointItems = uniqueWaypointIds.map((waypointId) => {
        const booking = bookings.find(
          (b) => b.pickup_stop_id === waypointId || b.dropoff_stop_id === waypointId,
        );
        const isPickup = booking?.pickup_stop_id === waypointId;
        return {
          id: waypointId,
          fields: {
            name: isPickup ? booking?.pickup_name || '' : booking?.dropoff_name || '',
            address: isPickup ? booking?.pickup_address || '' : booking?.dropoff_address || '',
          },
        };
      });

      try {
        const [routeTranslations, waypointTranslations] = await Promise.all([
          translationService.batchGetTranslations(currentEntity.entityId, 'route', routeItems, i18n.language),
          waypointItems.length
            ? translationService.batchGetTranslations(currentEntity.entityId, 'waypoint', waypointItems, i18n.language)
            : Promise.resolve(new Map()),
        ]);

        return bookings.map((booking) => {
          const routeTranslation = routeTranslations.get(booking.route_id);
          const pickupTranslation = waypointTranslations.get(booking.pickup_stop_id);
          const dropoffTranslation = waypointTranslations.get(booking.dropoff_stop_id);

          return {
            ...booking,
            route_name: routeTranslation?.name?.content || booking.route_name,
            pickup_name: pickupTranslation?.name?.content || booking.pickup_name,
            pickup_address: pickupTranslation?.address?.content || booking.pickup_address,
            dropoff_name: dropoffTranslation?.name?.content || booking.dropoff_name,
            dropoff_address: dropoffTranslation?.address?.content || booking.dropoff_address,
          };
        });
      } catch (error) {
        console.warn('Failed to translate booking details:', error);
        return bookings;
      }
    },
    [
      currentEntity?.entityId,
      currentEntity?.entity_default_language,
      entitySettings?.entity_default_language,
      entitySettings?.multi_language_enabled,
      i18n.language,
    ],
  );

  const fetchUserBookings = useCallback(async () => {
    if (!currentEntity?.entityId) return [];

    try {
      const response = await axios.get(`/api/bookings/${currentEntity.entityId}/user`);
      const bookings = await applyBookingTranslations(response.data);
      setUserBookings(bookings);
      return bookings;
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error(t('Failed to fetch bookings'));
      return [];
    }
  }, [applyBookingTranslations, currentEntity?.entityId, t]);

  const fetchRoutes = useCallback(async () => {
    if (!currentEntity?.entityId) return [];

    try {
      setLoading(true);
      let fetchedRoutes = await fetchEntityRoutes(currentEntity.entityId, {
        includeInactive: false,
        include: 'stops',
      });
      fetchedRoutes = await applyRouteTranslations(fetchedRoutes);
      // Fetch reference data (GPS logs) and segment data (OpenRouteService) in parallel
      await Promise.all([
        fetchAllReferenceData(fetchedRoutes),
        fetchAllSegmentData(fetchedRoutes),
      ]);
      setRoutes(fetchedRoutes);
      return fetchedRoutes;
    } catch (error) {
      console.error('Error fetching routes:', error);
      toast.error(t('errorFetchingRoutes'));
      return [];
    } finally {
      setLoading(false);
    }
  }, [applyRouteTranslations, currentEntity?.entityId, fetchAllReferenceData, fetchAllSegmentData, t]);

  useEffect(() => {
    if (!currentEntity?.entityId) {
      setRoutes([]);
      setReferenceDataMap({});
      setSegmentDataMap({});
      return;
    }

    fetchRoutes();
  }, [currentEntity?.entityId, entitySettings, fetchRoutes]);

  useEffect(() => {
    if (!currentEntity?.entityId) {
      setUserBookings([]);
      return;
    }

    fetchUserBookings();
  }, [currentEntity?.entityId, entitySettings, fetchUserBookings]);

  useEffect(() => {
    const handleLanguageChange = () => {
      if (!currentEntity?.entityId || !entitySettings) return;
      translationService.clearCache();
      fetchRoutes();
      fetchUserBookings();
    };

    i18n?.on('languageChanged', handleLanguageChange);
    return () => {
      i18n?.off('languageChanged', handleLanguageChange);
    };
  }, [currentEntity?.entityId, entitySettings, fetchRoutes, fetchUserBookings, i18n]);

  return {
    routes,
    referenceDataMap,
    segmentDataMap, // Segment times for baseline ETA calculation
    userBookings,
    loading,
    setLoading,
    setUserBookings,
    fetchRoutes,
    fetchUserBookings,
  };
};

export default useBookingManagement;
